import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Home, Info, Mail,  LifeBuoy, ChevronDown, X, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [cartCount, setCartCount] = useState(0); // Estado para el contador del carrito

  const mainRoutes = [
    { name: 'Home', path: '/', icon: <Home className="w-5 h-5" /> },
    { name: 'About', path: '/about', icon: <Info className="w-5 h-5" /> },
    { name: 'Blog', path: '/templates/blog', icon: <Mail className="w-5 h-5" /> },
    { name: 'Templates', path: '/templates', icon: <Layout className="w-5 h-5" /> },
    { name: 'Assets', path: '/assets', icon: <Layout className="w-5 h-5" /> },
  ];

  const dropdownRoutes = {
    support: [
      { name: 'Documentation', path: '/templates/installations/documentation', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'Support', path: '/templates/support/contact', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'FAQS', path: '/faq', icon: <LifeBuoy className="w-5 h-5" /> },
    ],
    account: [
      { name: 'Login', path: '/login', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'EnterPrise', path: '/enterprise', icon: <LifeBuoy className="w-5 h-5" /> },
    ],
    resources: [
      { name: 'Contact', path: '/contact', icon: <Mail className="w-5 h-5" /> }, // Moved to dropdown
      { name: 'Partners', path: '/partners', icon: <Mail className="w-5 h-5" /> }, // Moved to dropdown
      { name: 'Pricing Guide', path: '/pricing-suggestions', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'Services', path: '/additionals/services', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'FeedBack', path: '/feedback', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'Suggest a Template', path: '/template-request', icon: <LifeBuoy className="w-5 h-5" /> },
      { name: 'Pricing', path: '/pricing', icon: <LifeBuoy className="w-5 h-5" /> },
    ],
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (routePath: string) => {
    return location.pathname === routePath || 
           (routePath !== '/' && location.pathname.startsWith(routePath));
  };

  const toggleDropdown = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  return (
    <nav className={`sticky top-0 z-[105000] bg-white/90 backdrop-blur-md transition-shadow ${isScrolled ? 'shadow-md' : 'shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" aria-label="PixelCraft Home">
            <svg
              className="w-8 h-8 text-navy-900 group-hover:text-indigo-700 transition-colors"
              viewBox="0 0 32 32"
              fill="currentColor"
            >
              <path d="M16 2.667l-12 8v10.666l12 8 12-8V10.667l-12-8zm0 2.666l9.333 6.222v8.888L16 25.333 6.667 20.443v-8.888L16 5.333zM16 12l-8 5.333v-4.666L16 8l8 5.333v4.666L16 20l-8-5.333v4.666L16 24l8-5.333v-4.666L16 12z" />
            </svg>
            <span className="font-bold text-2xl bg-clip-text text-transparent bg-gradient-to-r from-navy-900 to-navy-700">
              PixelCraft
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {mainRoutes.map((route) => (
              <Link
                key={route.path}
                to={route.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  isActive(route.path)
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-indigo-600'
                }`}
              >
                <span className="relative group">
                  {route.name}
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 transition-all ${
                      isActive(route.path) ? 'w-full' : 'group-hover:w-full'
                    }`}
                  ></span>
                </span>
              </Link>
            ))}

            {/* Dropdowns */}
            {Object.entries(dropdownRoutes).map(([dropdownName, routes]) => (
              <div 
                key={dropdownName}
                className="relative z-[1000]"
                onMouseEnter={() => toggleDropdown(dropdownName)}
                onMouseLeave={() => toggleDropdown(dropdownName)}
              >
                <button
                  className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1 transition-all ${
                    openDropdown === dropdownName || routes.some(route => isActive(route.path))
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-indigo-600'
                  }`}
                  onClick={() => toggleDropdown(dropdownName)}
                >
                  {dropdownName.charAt(0).toUpperCase() + dropdownName.slice(1)}
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === dropdownName ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {openDropdown === dropdownName && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-[1001]"
                    >
                      <div className="py-2">
                        {routes.map((route) => (
                          <Link
                            key={route.path}
                            to={route.path}
                            className={`flex items-center px-4 py-2 text-sm transition-colors ${
                              isActive(route.path)
                                ? 'bg-indigo-50 text-indigo-700'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {route.icon}
                            <span className="ml-2">{route.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            {/* Cart Icon with Counter */}
            <Link
              to="/cart"
              className="relative p-2 rounded-md hover:bg-gray-50 transition-colors"
            >
              <ShoppingCart className="w-6 h-6 text-gray-600" />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-indigo-600 text-white text-xs rounded-full px-1.5 py-0.5">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 z-[1000]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Open Menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Layout className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[1001] md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setIsMenuOpen(false)}
            ></div>
            <motion.div
              className="relative bg-white w-64 h-full shadow-xl z-[1002]"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            >
              <div className="flex flex-col h-full">
                <nav className="flex-1 px-4 py-6 space-y-2">
                  {[...mainRoutes, ...Object.values(dropdownRoutes).flat()].map((route) => (
                    <Link
                      key={route.path}
                      to={route.path}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive(route.path)
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {route.icon}
                      <span className="text-sm font-medium">{route.name}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}